<template>
  <div class="container-fluid">
    <div class="page-title">
      <h3>Escrow de pagos</h3>
    </div>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h4 class="mb-0">Fondos retenidos pendientes de liberar</h4>
        <div class="d-flex gap-2 align-items-center">
          <report-export-buttons
            title="Reporte de escrow"
            subtitle="Fondos retenidos pendientes de liberar"
            file-name="reporte-escrow"
            :columns="reportColumns"
            :rows="reportRows"
            :disabled="loading"
          />
          <button class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="loadPending">
            Recargar
          </button>
        </div>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>Paquete</th>
                <th>Estatus paquete</th>
                <th>Estado de liberación</th>
                <th>Monto cobrado</th>
                <th>Le toca viajero</th>
                <th>Comisión plataforma</th>
                <th>Pago</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.paymentId">
                <td>{{ shortId(row.packageId) }}</td>
                <td>{{ row.packageStatus }}</td>
                <td>
                  <span class="badge bg-warning text-dark">Pendiente de liberación</span>
                </td>
                <td>{{ formatMxn(row.amount) }}</td>
                <td>{{ formatMxn(row.travelerAmountMxn) }}</td>
                <td>{{ formatMxn(row.platformFeeMxn) }}</td>
                <td>{{ shortId(row.paymentId) }}</td>
                <td class="text-end">
                  <button
                    class="btn btn-success btn-sm"
                    :disabled="releasing[row.paymentId]"
                    @click="release(row.paymentId)"
                  >
                    {{ releasing[row.paymentId] ? 'Liberando...' : 'Liberar fondos' }}
                  </button>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="8" class="text-center text-muted py-4">No hay fondos en retención.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentsApi } from '@/api/payments';
import Swal from 'sweetalert2';
import ReportExportButtons from '@/components/reports/ReportExportButtons.vue';

export default {
  name: 'EscrowPagos',
  components: { ReportExportButtons },
  data() {
    return {
      loading: false,
      rows: [],
      releasing: {},
    };
  },
  mounted() {
    this.loadPending();
  },
  computed: {
    reportColumns() {
      return [
        { key: 'packageId', label: 'Paquete' },
        { key: 'packageStatus', label: 'Estatus paquete' },
        { key: 'releaseStatus', label: 'Estado de liberación' },
        { key: 'amount', label: 'Monto cobrado' },
        { key: 'travelerAmountMxn', label: 'Le toca viajero' },
        { key: 'platformFeeMxn', label: 'Comisión plataforma' },
        { key: 'paymentId', label: 'Pago' },
      ];
    },
    reportRows() {
      return this.rows.map((row) => ({
        packageId: this.shortId(row.packageId),
        packageStatus: row.packageStatus,
        releaseStatus: 'Pendiente de liberación',
        amount: this.formatMxn(row.amount),
        travelerAmountMxn: this.formatMxn(row.travelerAmountMxn),
        platformFeeMxn: this.formatMxn(row.platformFeeMxn),
        paymentId: this.shortId(row.paymentId),
      }));
    },
  },
  methods: {
    async loadPending() {
      this.loading = true;
      try {
        const { data } = await paymentsApi.getEscrowPending();
        this.rows = Array.isArray(data) ? data : [];
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo cargar el escrow.';
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.loading = false;
      }
    },
    async release(paymentId) {
      this.releasing = { ...this.releasing, [paymentId]: true };
      try {
        const { data } = await paymentsApi.releaseEscrow(paymentId);
        if (data?.status === 'pending') {
          await Swal.fire({
            icon: 'info',
            title: 'Liberación en proceso',
            text: 'La transferencia al viajero fue creada en Stripe y está en proceso.',
            confirmButtonColor: '#0d6efd',
          });
        } else {
          await Swal.fire({
            icon: 'success',
            title: 'Fondos liberados',
            text: 'Se liberaron correctamente los fondos al viajero.',
            confirmButtonColor: '#0d6efd',
          });
        }
        await this.loadPending();
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo liberar el pago.';
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo liberar',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.releasing = { ...this.releasing, [paymentId]: false };
      }
    },
    shortId(id) {
      return String(id || '').slice(0, 8);
    },
    formatMxn(amount) {
      const n = Number(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(n);
    },
  },
};
</script>

